// Clause coverage of a grid code version: extraction-status counts folded into the stack bar segments and legend (extracted and reused, informative, pending, failed, not in scope).
import type { LegendItem } from "@/components/common/Legend";
import type { StackSegment } from "@/components/common/StackBar";
import { formatNumber } from "@/lib/format";

export interface Coverage {
  extracted: number;
  informative: number;
  pending: number;
  failed: number;
  notInScope: number;
  total: number;
}

export const COVERAGE_COLORS = {
  extracted: "var(--brand-blue)",
  informative: "var(--b3)",
  pending: "var(--stack-other)",
  failed: "var(--brand-red)",
  notInScope: "var(--border)",
} as const;

function count(byStatus: Readonly<Record<string, number | undefined>>, key: string): number {
  return byStatus[key] ?? 0;
}

export function coverageFromStatuses(byStatus: Readonly<Record<string, number | undefined>>): Coverage {
  const coverage = {
    extracted: count(byStatus, "extracted") + count(byStatus, "reused"),
    informative: count(byStatus, "informative"),
    pending: count(byStatus, "pending"),
    failed: count(byStatus, "failed"),
    notInScope: count(byStatus, "not_in_scope"),
  };
  return {
    ...coverage,
    total:
      coverage.extracted + coverage.informative + coverage.pending + coverage.failed + coverage.notInScope,
  };
}

export function coverageSegments(coverage: Coverage): StackSegment[] {
  return [
    { key: "extracted", label: "extracted", value: coverage.extracted, color: COVERAGE_COLORS.extracted },
    {
      key: "informative",
      label: "informative",
      value: coverage.informative,
      color: COVERAGE_COLORS.informative,
    },
    { key: "pending", label: "pending", value: coverage.pending, color: COVERAGE_COLORS.pending },
    { key: "failed", label: "failed", value: coverage.failed, color: COVERAGE_COLORS.failed },
    {
      key: "not-in-scope",
      label: "not in scope",
      value: coverage.notInScope,
      color: COVERAGE_COLORS.notInScope,
    },
  ];
}

export function coverageLegend(coverage: Coverage): LegendItem[] {
  const items: LegendItem[] = [
    {
      key: "extracted",
      label: `${formatNumber(coverage.extracted)} extracted`,
      color: COVERAGE_COLORS.extracted,
    },
    {
      key: "informative",
      label: `${formatNumber(coverage.informative)} informative`,
      color: COVERAGE_COLORS.informative,
    },
    { key: "pending", label: `${formatNumber(coverage.pending)} pending`, color: COVERAGE_COLORS.pending },
  ];
  if (coverage.failed > 0) {
    items.push({
      key: "failed",
      label: `${formatNumber(coverage.failed)} failed`,
      color: COVERAGE_COLORS.failed,
    });
  }
  return items;
}

export function coverageSummary(coverage: Coverage): string {
  const parts = [
    `${formatNumber(coverage.extracted)} extracted`,
    `${formatNumber(coverage.informative)} informative`,
    `${formatNumber(coverage.pending)} pending`,
  ];
  if (coverage.failed > 0) {
    parts.push(`${formatNumber(coverage.failed)} failed`);
  }
  return parts.join(" · ");
}
